import { memo, useMemo, type FC } from "react";
import { useTranslation } from "react-i18next";
import StyledTextArea from "src/components/reusable/StyledTextArea";
import CampaignStepCard from "../../../../shared/CampaignStepCard";
import CampaignFormField from "../../../../shared/CampaignFormField";
import CampaignTextField from "../../../../shared/CampaignTextField";
import CampaignSelectField from "../../../../shared/CampaignSelectField";
import { getFieldErrorId, useRequiredFieldError } from "../../../../shared/UseRequiredFieldError";
import { TONE_KEYS, getToneLabelKey } from "../AiCampaignConstants";
import type { AiCampaignForm, ToneKey, UpdateAiCampaignForm } from "../AiCampaignTypes";

const STEP_KEY = "campaignFlow.aiWizard.aiPrompt";
const GOAL_ROWS = 3;

const FIELD_IDS = {
  goal: "ai-prompt-goal",
  tone: "ai-prompt-tone",
  persona: "ai-prompt-persona",
  seasonalTag: "ai-prompt-seasonal-tag",
  offer: "ai-prompt-offer",
  ctaUrl: "ai-prompt-cta-url",
  ctaLabel: "ai-prompt-cta-label",
};

interface AiPromptStepProps {
  form: AiCampaignForm;
  updateForm: UpdateAiCampaignForm;
}

const AiPromptStep: FC<AiPromptStepProps> = ({ form, updateForm }) => {
  const { t } = useTranslation();
  const goalField = useRequiredFieldError(form.prompt, true);

  const toneOptions = useMemo(
    () => TONE_KEYS.map(tone => ({ value: tone, label: t(getToneLabelKey(tone)) })),
    [t]
  );

  return (
    <CampaignStepCard title={t(`${STEP_KEY}.title`)} subtitle={t(`${STEP_KEY}.subtitle`)}>
      <CampaignFormField
        label={t(`${STEP_KEY}.goal`)}
        htmlFor={FIELD_IDS.goal}
        required
        hasError={goalField.hasError}
      >
        <StyledTextArea
          id={FIELD_IDS.goal}
          value={form.prompt}
          onChange={event => updateForm("prompt", event.target.value)}
          onBlur={goalField.handleBlur}
          aria-invalid={goalField.hasError}
          aria-describedby={goalField.hasError ? getFieldErrorId(FIELD_IDS.goal) : undefined}
          placeholder={t(`${STEP_KEY}.goalPlaceholder`)}
          rows={GOAL_ROWS}
          className="campaign-flow__textarea"
        />
      </CampaignFormField>

      <CampaignSelectField
        id={FIELD_IDS.tone}
        label={t(`${STEP_KEY}.tone`)}
        value={form.tone}
        options={toneOptions}
        onChange={value => updateForm("tone", value as ToneKey)}
        placeholder={t(`${STEP_KEY}.tonePlaceholder`)}
        required
      />

      <CampaignTextField
        id={FIELD_IDS.persona}
        label={t(`${STEP_KEY}.persona`)}
        value={form.persona}
        onChange={value => updateForm("persona", value)}
        placeholder={t(`${STEP_KEY}.personaPlaceholder`)}
        required
      />

      <CampaignTextField
        id={FIELD_IDS.seasonalTag}
        label={t(`${STEP_KEY}.seasonalTag`)}
        value={form.seasonalTag}
        onChange={value => updateForm("seasonalTag", value)}
        placeholder={t(`${STEP_KEY}.seasonalTagPlaceholder`)}
        required
      />

      <CampaignTextField
        id={FIELD_IDS.offer}
        label={t(`${STEP_KEY}.offer`)}
        hint={t("campaignFlow.createCampaignNew.optionalHint")}
        value={form.offer}
        onChange={value => updateForm("offer", value)}
        placeholder={t(`${STEP_KEY}.offerPlaceholder`)}
      />

      <div className="campaign-flow__field-row">
        <CampaignTextField
          id={FIELD_IDS.ctaUrl}
          label={t(`${STEP_KEY}.ctaUrl`)}
          value={form.ctaUrl}
          onChange={value => updateForm("ctaUrl", value)}
          placeholder={t(`${STEP_KEY}.ctaUrlPlaceholder`)}
          required
        />
        <CampaignTextField
          id={FIELD_IDS.ctaLabel}
          label={t(`${STEP_KEY}.ctaLabel`)}
          value={form.ctaLabel}
          onChange={value => updateForm("ctaLabel", value)}
          placeholder={t(`${STEP_KEY}.ctaLabelPlaceholder`)}
          required
        />
      </div>
    </CampaignStepCard>
  );
};

export default memo(AiPromptStep);
